/**
 * Shared Festschreibung controls — project Live + Admin Locks tab (PRD §7.2).
 * One lock row = one inclusive month interval within a calendar year.
 */

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Lock, Unlock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { YearSelect } from "@/components/ui/year-select";
import { describeError } from "@/lib/errors";
import { formatProjectTerm } from "@/lib/format";
import { useLockMonth, useProjectLocks, useUnlockMonth } from "@/lib/tracking";
import { berlinToday } from "@/shared/dates";
import { clampToYears, projectMonthsInYear, projectYears } from "@/shared/years";
import type { MonthLockSummary } from "@/shared/schemas/tracking";

const ALL_MONTHS = Array.from({ length: 12 }, (_unused, index) => index + 1);

export interface MonthLockPanelProps {
  projectId: string;
  /** Year used when locking an interval. */
  year: number;
  onYearChange?: (year: number) => void;
  /** Show the year picker next to the month selects (Admin). */
  yearEditable?: boolean;
  /** The project's term. Given, only years and months inside it can be picked. */
  projectStartDate?: string;
  projectEndDate?: string;
  /** Include unlocked historical locks in the list. */
  history?: boolean;
  canUnlock: boolean;
  className?: string;
}

function intervalsOverlap(aFrom: number, aTo: number, bFrom: number, bTo: number): boolean {
  return aFrom <= bTo && aTo >= bFrom;
}

export function MonthLockPanel({
  projectId,
  year,
  onYearChange,
  yearEditable = false,
  projectStartDate,
  projectEndDate,
  history = false,
  canUnlock,
  className,
}: MonthLockPanelProps) {
  const { t, i18n } = useTranslation();
  const today = berlinToday();
  const todayMonth = Number(today.slice(5, 7));
  const [fromMonth, setFromMonth] = useState(todayMonth);
  const [toMonth, setToMonth] = useState(todayMonth);
  const [unlocking, setUnlocking] = useState<MonthLockSummary | null>(null);
  const [unlockReason, setUnlockReason] = useState("");

  // Nothing outside the term is lockable, so the pickers offer the term only and the panel works
  // off those values rather than whatever the parent still holds after a project switch.
  const years =
    projectStartDate && projectEndDate ? projectYears(projectStartDate, projectEndDate) : [];
  const lockYear = years.length > 0 ? clampToYears(year, years) : year;
  const monthOptions =
    projectStartDate && projectEndDate
      ? projectMonthsInYear(lockYear, projectStartDate, projectEndDate)
      : ALL_MONTHS;
  const selectedFrom = monthOptions.includes(fromMonth) ? fromMonth : (monthOptions[0] ?? 1);
  const toOptions = monthOptions.filter((month) => month >= selectedFrom);
  const selectedTo = toOptions.includes(toMonth)
    ? toMonth
    : (toOptions[toOptions.length - 1] ?? selectedFrom);

  const locksQuery = useProjectLocks(projectId, {
    year: history ? undefined : lockYear,
    history,
  });
  const locks = locksQuery.data ?? [];
  const lock = useLockMonth(projectId);
  const unlock = useUnlockMonth(projectId);

  const locale = i18n.language === "en" ? "en-US" : "de-DE";
  const monthsShort = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) =>
        new Date(2024, i, 1).toLocaleDateString(locale, { month: "short" }),
      ),
    [locale],
  );

  const activeLocks = locks.filter((row) => row.unlockedAt === null);
  const rangeAlreadyLocked = activeLocks.some(
    (row) =>
      row.year === lockYear &&
      intervalsOverlap(row.fromMonth, row.toMonth, selectedFrom, selectedTo),
  );

  function formatWhen(iso: string): string {
    return new Date(iso).toLocaleString(locale);
  }

  function formatInterval(row: MonthLockSummary): string {
    const from = String(row.fromMonth).padStart(2, "0");
    const to = String(row.toMonth).padStart(2, "0");
    if (row.fromMonth === row.toMonth) {
      return `${row.year}-${from}`;
    }
    return `${row.year}-${from} – ${row.year}-${to}`;
  }

  return (
    <section className={className ?? "rounded-2xl border border-border bg-card p-5"}>
      <h2 className="flex items-center gap-2 text-lg">
        <Lock className="size-4" /> {t("monthLock.title")}
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">{t("monthLock.body")}</p>

      <div className="mt-4 flex flex-wrap items-end gap-3">
        {yearEditable && (
          <div className="space-y-1.5">
            <Label>{t("monthLock.selectYear")}</Label>
            <YearSelect
              value={lockYear}
              onChange={(next) => onYearChange?.(next)}
              years={years}
              ariaLabel={t("monthLock.selectYear")}
            />
          </div>
        )}
        <div className="space-y-1.5">
          <Label>{t("monthLock.selectFromMonth")}</Label>
          <Select
            value={String(selectedFrom)}
            onValueChange={(value) => {
              const next = Number(value);
              setFromMonth(next);
              if (toMonth < next) setToMonth(next);
            }}
          >
            <SelectTrigger className="w-40" aria-label={t("monthLock.selectFromMonth")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {monthOptions.map((month) => (
                <SelectItem key={month} value={String(month)}>
                  {monthsShort[month - 1]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>{t("monthLock.selectToMonth")}</Label>
          <Select value={String(selectedTo)} onValueChange={(value) => setToMonth(Number(value))}>
            <SelectTrigger className="w-40" aria-label={t("monthLock.selectToMonth")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {toOptions.map((month) => (
                <SelectItem key={month} value={String(month)}>
                  {monthsShort[month - 1]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          disabled={lock.isPending || rangeAlreadyLocked || !Number.isInteger(lockYear)}
          onClick={() =>
            lock.mutate(
              { year: lockYear, fromMonth: selectedFrom, toMonth: selectedTo },
              {
                onSuccess: () =>
                  toast.success(
                    t("monthLock.lockedToast", {
                      fromMonth: selectedFrom,
                      toMonth: selectedTo,
                      year: lockYear,
                    }),
                  ),
                onError: (error) => toast.error(describeError(error)),
              },
            )
          }
        >
          {t("monthLock.lock")}
        </Button>
      </div>

      {projectStartDate && projectEndDate && monthOptions.length < 12 && (
        <p className="mt-2 text-xs text-muted-foreground">
          {t("monthLock.outsideTerm", {
            term: formatProjectTerm(projectStartDate, projectEndDate),
          })}
        </p>
      )}

      <ul className="mt-4 space-y-2">
        {locks.length === 0 && (
          <li className="text-sm text-muted-foreground">{t("monthLock.noLocks")}</li>
        )}
        {locks.map((row) => {
          const active = row.unlockedAt === null;
          return (
            <li
              key={row.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border px-3 py-2 text-sm"
            >
              <div>
                <div className="font-medium">
                  {formatInterval(row)} {t("monthLock.entireProject")}
                  {!active && (
                    <span className="ml-2 text-xs font-normal text-muted-foreground">
                      · {t("monthLock.unlockedBadge")}
                    </span>
                  )}
                  {active && (
                    <span className="ml-2 text-xs font-normal text-muted-foreground">
                      · {t("monthLock.lockedBadge")}
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t("monthLock.lockedOn", { when: formatWhen(row.lockedAt) })}
                  {!active && row.unlockedAt && (
                    <>
                      {" · "}
                      {t("monthLock.unlockedOn", {
                        when: formatWhen(row.unlockedAt),
                        reason: row.unlockReason ?? "",
                      })}
                    </>
                  )}
                </div>
              </div>
              {active &&
                (canUnlock ? (
                  <Button variant="outline" size="sm" onClick={() => setUnlocking(row)}>
                    <Unlock className="size-3.5" /> {t("monthLock.unlock")}
                  </Button>
                ) : (
                  <span className="text-xs text-muted-foreground">
                    {t("monthLock.caCannotUnlock")}
                  </span>
                ))}
            </li>
          );
        })}
      </ul>

      <Dialog
        open={Boolean(unlocking)}
        onOpenChange={(open) => {
          if (!open) {
            setUnlocking(null);
            setUnlockReason("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("monthLock.unlockTitle")}</DialogTitle>
            <DialogDescription>{t("monthLock.unlockBody")}</DialogDescription>
          </DialogHeader>
          <Textarea
            value={unlockReason}
            onChange={(event) => setUnlockReason(event.target.value)}
            placeholder={t("monthLock.reasonPlaceholder")}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnlocking(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              disabled={unlockReason.trim().length < 3 || unlock.isPending}
              onClick={() => {
                if (!unlocking) return;
                if (unlockReason.trim().length < 3) {
                  toast.error(t("monthLock.reasonTooShort"));
                  return;
                }
                unlock.mutate(
                  { lockId: unlocking.id, reason: unlockReason.trim() },
                  {
                    onSuccess: () => {
                      toast.success(
                        t("monthLock.unlockedToast", {
                          fromMonth: unlocking.fromMonth,
                          toMonth: unlocking.toMonth,
                          year: unlocking.year,
                        }),
                      );
                      setUnlocking(null);
                      setUnlockReason("");
                    },
                    onError: (error) => toast.error(describeError(error)),
                  },
                );
              }}
            >
              {t("monthLock.unlock")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
